import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Paper,
  Stack,
  Typography,
  Select,
  MenuItem,
  Button,
  Box,
  Autocomplete,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Skeleton,
} from "@mui/material";
import ThemedSelect from "../../utils/ThemedSelect";
import ErrorModal from "../../utils/ErrorModal";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#D5DEE7";
const PRIMARY = "#0B5C8A";

const fieldSx = {
  "& .MuiOutlinedInput-root, &.MuiOutlinedInput-root": {
    minHeight: 42,
    borderRadius: "10px",
    background: "#FFFFFF",
    fontFamily: FONT,
    fontSize: 14,
    color: INK,
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: FIELD_EDGE },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, &.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
    { borderColor: "#B4C6DA" },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline":
    { borderColor: `${PRIMARY} !important`, borderWidth: "1px !important" },
};
const menuPaperSx = {
  mt: 0.5,
  borderRadius: "12px",
  border: `1px solid ${LINE}`,
  boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)",
  "& .MuiList-root": { p: 0.5 },
  "& .MuiMenuItem-root, & .MuiAutocomplete-option": {
    fontFamily: FONT,
    fontSize: 13.5,
    borderRadius: "8px",
    minHeight: 38,
    mx: 0.5,
  },
  "& .MuiMenuItem-root.Mui-selected": {
    background: "#EAF2FB",
    color: "#1B4F82",
    fontWeight: 700,
  },
};
const chipSx = {
  height: 26,
  borderRadius: "999px",
  background: "#EAF2FB",
  border: "1px solid #C9DEF3",
  color: "#1B4F82",
  fontFamily: FONT,
  fontSize: 12.5,
  fontWeight: 600,
  "& .MuiChip-deleteIcon": {
    fontSize: 16,
    color: "#1B4F82",
    opacity: 0.6,
    "&:hover": { color: "#1B4F82", opacity: 1 },
  },
};
const noteSx = {
  fontFamily: FONT,
  fontSize: 13,
  lineHeight: 1.5,
  color: "#4A5A6E",
  textWrap: "pretty",
};

// NOTE: adjust this list if your role set differs — pulled from the roles
// used across ProtectedRoute/App.jsx (ADMIN, VT1, VT2, TELECALLER,
// SALESPERSON, PROJECT_HEAD, MARKETER).
const ROLE_OPTIONS = [
  "ADMIN",
  "VT1",
  "VT2",
  "TELECALLER",
  "SALESPERSON",
  "PROJECT_HEAD",
  "MARKETER",
];

const STRATEGY_OPTIONS = [
  { value: "ADMIN", label: "Admin only (default)" },
  { value: "DIRECT", label: "Direct assignment — one specific person" },
  { value: "ROUND_ROBIN_ALL", label: "Round robin — all team members" },
  { value: "ROUND_ROBIN_ROLE", label: "Round robin — specific role(s)" },
];

const DefaultAssigneeSection = ({ project, onUpdated }) => {
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);

  const [strategy, setStrategy] = useState(
    project?.assignmentStrategy || "ADMIN",
  );
  const [selectedUserId, setSelectedUserId] = useState(
    project?.assignmentStrategy === "DIRECT" ? project?.defaultAssignee : null,
  );
  const [selectedRoles, setSelectedRoles] = useState(
    project?.assignmentRoles || [],
  );
  const [saving, setSaving] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("success");
  const afterClose = useRef(null);

  const showModal = (msg, type, onClose = null) => {
    setModalMsg(msg);
    setModalType(type);
    afterClose.current = onClose;
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    const next = afterClose.current;
    afterClose.current = null;
    if (next) next();
  };

  const teamId = project?.team?.teamId;

  useEffect(() => {
    setTeamMembers(project?.team?.orgUsers || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const eligibleForRoles = teamMembers.filter((u) =>
    selectedRoles.includes(u.userRoleName),
  );

  const handleSave = async () => {
    if (strategy === "DIRECT" && !selectedUserId) {
      showModal("Please select a user for direct assignment.", "error");
      return;
    }
    if (strategy === "ROUND_ROBIN_ALL" && teamMembers.length === 0) {
      showModal("Cannot use round-robin assignment when the team has no members.", "error");
      return;
    }
    if (strategy === "ROUND_ROBIN_ROLE" && selectedRoles.length === 0) {
      showModal("Please select at least one role.", "error");
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/project/default-assignee?projectId=${project.projectId}`,
        {
          strategy,
          userId: strategy === "DIRECT" ? selectedUserId : null,
          roles: strategy === "ROUND_ROBIN_ROLE" ? selectedRoles : null,
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      showModal("Default assignee settings saved.", "success", () => {
        if (onUpdated) onUpdated();
      });
    } catch (err) {
      console.log(err);
      showModal(
        err?.response?.data?.message ||
          "Failed to save default assignee settings.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 3,
        p: 0,
        background: "#FFFFFF",
        border: `1px solid ${LINE}`,
        borderRadius: "14px",
        overflow: "hidden",
        fontFamily: FONT,
        color: INK,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.125,
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #EDF1F5",
        }}
      >
        <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", background: "#1F6FEB", flexShrink: 0 }} />
        <Typography variant="h6" mb={0} sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em", color: INK }}>
          Default Lead Assignment
        </Typography>
      </Box>

      <Box sx={{ px: 2, pt: 1.75, pb: 2 }}>
        <Typography variant="body2" color="text.secondary" mb={1.75} sx={noteSx}>
          Decide who new leads for this project get assigned to automatically.
        </Typography>

        <label
          style={{ display: "block", marginBottom: 6, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: INK_2 }}
        >
          Strategy
        </label>
        <ThemedSelect
          value={strategy}
          onChange={(e) => {
            setStrategy(e.target.value);
            setSelectedUserId(null);
            setSelectedRoles([]);
          }}
          options={STRATEGY_OPTIONS}
          sx={{ mb: 1.75 }}
        />

        {loadingTeam ? (
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: "10px", bgcolor: "#F1F4F8" }} />
        ) : !teamId ? (
          <Typography
            color="text.secondary"
            fontSize={14}
            sx={{
              ...noteSx,
              p: 1.5,
              background: "#FDF4E1",
              border: "1px solid #F3E0B4",
              borderRadius: "10px",
            }}
          >
            This project has no team assigned, so only "Admin only" is available.
            Assign a team to this project to enable direct or round-robin
            assignment.
          </Typography>
        ) : (
          <>
            {strategy === "DIRECT" && (
              <Autocomplete
                options={teamMembers}
                getOptionLabel={(u) => `${u.name} (${u.userRoleName})`}
                value={
                  teamMembers.find((u) => u.orgUserId === selectedUserId) || null
                }
                onChange={(e, value) =>
                  setSelectedUserId(value?.orgUserId || null)
                }
                isOptionEqualToValue={(a, b) => a.orgUserId === b.orgUserId}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select team member" sx={fieldSx} />
                )}
                slotProps={{ paper: { sx: menuPaperSx } }}
              />
            )}

            {strategy === "ROUND_ROBIN_ROLE" && (
              <>
                <Autocomplete
                  multiple
                  options={project?.team?.orgUsers.map((u) => u.userRoleName)}
                  value={selectedRoles}
                  onChange={(e, value) => setSelectedRoles(value)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                        sx={chipSx}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select role(s)" sx={fieldSx} />
                  )}
                  slotProps={{ paper: { sx: menuPaperSx } }}
                />
                <Typography
                  fontSize={12}
                  color="text.secondary"
                  mt={1}
                  sx={{ fontFamily: FONT, fontSize: 12.5, color: "#4A5A6E" }}
                >
                  {selectedRoles.length > 0
                    ? `${eligibleForRoles.length} team member(s) match the selected role(s) and will rotate.`
                    : "Select at least one role to see who's eligible."}
                </Typography>
              </>
            )}

            {strategy === "ROUND_ROBIN_ALL" && (
              <Box
                sx={{
                  border: `1px solid ${LINE}`,
                  borderRadius: "10px",
                  p: 1.5,
                  background: "#F7F9FC",
                }}
              >
                <Typography fontSize={13} fontWeight={600} mb={1} sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: INK }}>
                  Rotates through all {teamMembers.length} team member(s)
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {teamMembers.map((u) => (
                    <Chip
                      key={u.orgUserId}
                      label={u.name}
                      size="small"
                      sx={{
                        ...chipSx,
                        background: "#FFFFFF",
                        border: `1px solid ${LINE}`,
                        color: INK_2,
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {strategy === "ADMIN" && (
              <Typography fontSize={13} color="text.secondary" sx={noteSx}>
                All new leads will be assigned to the org admin by default.
              </Typography>
            )}
          </>
        )}

        <Button
          variant="contained"
          sx={{
            mt: 2,
            height: 38,
            px: 2.25,
            borderRadius: "9px",
            background: PRIMARY,
            color: "#FFFFFF",
            fontFamily: FONT,
            fontSize: 13.5,
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { background: "#084767", boxShadow: "none" },
            "&.Mui-disabled": { background: PRIMARY, color: "#FFFFFF", opacity: 0.6 },
          }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </Box>

      <ErrorModal
        open={modalOpen}
        handleClose={handleModalClose}
        msg={modalMsg}
        type={modalType}
      />
    </Paper>
  );
};

export default DefaultAssigneeSection;